import axios from 'axios';
import BaseApi from './BaseApi';
import {
	BrowseCertificateBaseViewType,
	AttachmentType,
	AttachmentWithSignDataType,
	SaveAttachmentInfoType,
	SignAttachmentInfoType,
	SignAttachmentResultType,
	SignHashArgType,
} from 'src/Types/File/Attachment';
import CertificateAPI from './Certificate';

type HashAlgIdResponse = { hashAlgId?: number };

type SendSoapArchiveDescriptionResponse = {
	hasError?: boolean;
	errorMessage?: string;
	digest2?: string;
	hashAlgId?: number;
	innerRequestSign?: string;
};

type SendSoapDownloadAttachmentsResponse = {
	hasError?: boolean;
	errorMessage?: string;
	digest1?: string;
	digest2?: string;
	hashAlgId?: number;
	innerRequestHash?: string;
	innerRequestFileName?: string;
};

type SendSoapDownloadAttachmentsSmev3Response = {
	hasError?: boolean;
	errorMessage?: string;
	digestArray?: string;
	hashAlgId?: number;
	controllerCallbackMethodName?: string;
};

type SendSoapCompleteArchiveResponse = {
	hasError?: boolean;
	errorMessage?: string;
	recid?: string;
	refreshattach?: boolean;
	refreshtask?: boolean;
	revision?: string | null;
};

export default class APIAttachment extends BaseApi {
	static getSimple = async (
		fileId: string,
		elementId: string,
		componentId?: string,
	): Promise<AttachmentWithSignDataType> => {
		const url =
			window.host +
			'/WebInterfaceModule/Attachment?fileId=' +
			fileId +
			'&attachmentId=' +
			encodeURIComponent(elementId) +
			'&needFillFiles=true';
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении данных задачи');
		}
	};

	static getSendSoap = async (
		fileId: string,
		elementId: string,
		componentId = '',
	): Promise<AttachmentType> => {
		const url =
			window.host +
			'/SendSoap/Attachment?fileId=' +
			fileId +
			'&attachmentId=' +
			encodeURIComponent(elementId) +
			'&componentId=' +
			componentId;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении данных задачи');
		}
	};

	static saveTitle = async (
		fileId: string,
		attachmentId: string,
		value: string,
	): Promise<SaveAttachmentInfoType> => {
		const url =
			window.host + '/WebInterfaceModule/File/Attachment/SaveTitle';
		const body = { fileId, attachmentId, value };
		try {
			const request = await axios.post(url, body, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при изменении наименования вложения');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при изменении наименования вложения');
		}
	};

	static saveDescription = async (
		fileId: string,
		attachmentId: string,
		value: string,
	): Promise<SaveAttachmentInfoType> => {
		const url =
			window.host + '/WebInterfaceModule/File/Attachment/SaveDescription';
		const body = { fileId, attachmentId, value };
		try {
			const request = await axios.post(url, body, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при изменении описания вложения');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при изменении описания вложения');
		}
	};

	static getHashFromServer = async (
		body: SignAttachmentInfoType,
	): Promise<BrowseCertificateBaseViewType> => {
		const url =
			window.host +
			'/WebInterfaceModule/File/Attachment/DownloadAttachmentAndSign';
		try {
			const request = await axios.post(url, body, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении хэша подписи файла');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении хэша подписи файла');
		}
	};

	static saveNewSignAttachment = async (
		requestBody: SignHashArgType,
	): Promise<SignAttachmentResultType> => {
		const url =
			window.host +
			'/WebInterfaceModule/File/Attachment/SaveNewSignAttachment';
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при изменении описания вложения');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при изменении описания вложения');
		}
	};

	static requestSendSoapSignAttachment = async (
		fileId: string,
		componentId: string,
		attachmentId: string,
	): Promise<string> => {
		const url = window.host + '/SendSoap/SignAttachment';
		const requestBody = { fileId, componentId, attachmentId };
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
			responseType: 'text',
		});
		return request.data;
	};

	static GetHashAlgId = async (
		requestBody: Record<string, unknown>,
	): Promise<HashAlgIdResponse> => {
		const url = window.host + '/SendSoap/GetHashAlgId';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static getSendSoapAttachmentsArchiveDescriptionHash = async (
		requestBody: Record<string, unknown>,
	): Promise<SendSoapArchiveDescriptionResponse> => {
		const url =
			window.host + '/SendSoap/GetAttachmentsArchiveDescriptionHash';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static getSendSoapAttachmentsArchiveDescriptionHashSmev3 = async (
		requestBody: Record<string, unknown>,
	): Promise<SendSoapArchiveDescriptionResponse> => {
		const url =
			window.host + '/SendSoap/GetAttachmentsArchiveDescriptionHashSmev3';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static downloadSendSoapAttachmentsAndSign = async (
		requestBody: Record<string, unknown>,
	): Promise<SendSoapDownloadAttachmentsResponse> => {
		const url = window.host + '/SendSoap/DownloadAttachmentsAndSign';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static downloadSendSoapAttachmentsSmev3AndSign = async (
		requestBody: Record<string, unknown>,
	): Promise<SendSoapDownloadAttachmentsSmev3Response> => {
		const url = window.host + '/SendSoap/DownloadAttachmentsSmev3AndSign';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static completeSendSoapAttachmentsArchive = async (
		requestBody: Record<string, unknown>,
	): Promise<SendSoapCompleteArchiveResponse> => {
		const url = window.host + '/SendSoap/CompleteAttachmentsArchive';
		const request = await axios.post(url, requestBody, {
			withCredentials: true,
		});
		return request.data;
	};

	static postSendSoapForm = async (
		url: string,
		formBody: URLSearchParams,
	): Promise<string> => {
		const request = await axios.post(window.host + url, formBody, {
			withCredentials: true,
			responseType: 'text',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		});
		return request.data;
	};

	static signAttachment = async (
		fileId: string,
		attachmentId: string,
		thumbprint: string,
		componentId?: string | null,
		addTimestamp = false,
	): Promise<SignAttachmentResultType> => {
		console.group('Start sign attachment');
		console.debug('Selected Certificate thumbprint - ', thumbprint);
		try {
			const certInfo =
				await CertificateAPI.getCertInfoByThumbprint(thumbprint);
			console.debug('Certificate info - ', certInfo);
			const getHashParams: SignAttachmentInfoType = {
				fileId,
				componentId,
				attachmentId,
				thumbprint,
				addTimestamp,
				certificate: certInfo.base64,
			};
			const { digest, hashAlgId } =
				await this.getHashFromServer(getHashParams);
			console.debug('Digest - ', digest, ' hashAlgId - ', hashAlgId);
			const signResult = await CertificateAPI.signHash(
				digest,
				hashAlgId,
				thumbprint,
			);
			console.debug('Sign hash result - ', signResult);

			if (!signResult.success) {
				return {
					hasError: true,
					errorMessage: signResult.message || 'Ошибка при подписании',
					recid: '',
					attachmentId: '',
					refreshtask: false,
					refreshattach: false,
					kickuser: false,
					revision: null,
				};
			}

			const requestBody = {
				fileId,
				attachmentId,
				componentId: componentId ?? '',
				certificate: certInfo.base64,
				thumbprint,
				hashAlgId,
				digest: signResult.data,
				addTimestamp,
			};
			const result = await this.saveNewSignAttachment(requestBody);
			console.debug('Result - ', result);
			return result;
		} catch (error: any) {
			console.warn(error);
			return {
				hasError: true,
				errorMessage:
					error.message ?? 'Ошибка при подписании вложения дела',
				recid: '',
				attachmentId: '',
				refreshtask: false,
				refreshattach: false,
				kickuser: false,
				revision: null,
			};
		} finally {
			console.groupEnd();
		}
	};
}
